import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

// Routes whose component (and its state — DevExtreme grid page/sort/scroll,
// local UI signals, etc.) should survive navigating away and back, instead
// of being destroyed and rebuilt from scratch. Needed for trade-contracts
// specifically because trade-contract-panel's "expand to full page" button
// navigates away to /trade-panel/:id and its "shrink" button navigates back
// — without this, that round trip reset the grid to page 1.
const CACHED_ROUTE_PATHS = ['trade-contracts'];

export class TradeContractRouteReuseStrategy implements RouteReuseStrategy {
  private readonly handles = new Map<string, DetachedRouteHandle>();

  // Same check Angular's own default strategy uses — config and params must
  // match — kept as-is for every route so only the detach/attach behavior
  // below (for the routes in CACHED_ROUTE_PATHS) differs from default.
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig && this.equalParams(future, curr);
  }

  private equalParams(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    const futureParams = future.params;
    const currParams = curr.params;
    const futureKeys = Object.keys(futureParams);
    const currKeys = Object.keys(currParams);
    return futureKeys.length === currKeys.length
      && futureKeys.every(key => futureParams[key] === currParams[key]);
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return CACHED_ROUTE_PATHS.includes(this.pathOf(route));
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    this.handles.set(this.pathOf(route), handle);
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this.handles.has(this.pathOf(route));
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this.handles.get(this.pathOf(route)) ?? null;
  }

  private pathOf(route: ActivatedRouteSnapshot): string {
    return route.routeConfig?.path ?? '';
  }
}
